import { makeAutoObservable } from 'mobx'
import { type IDropdownItem, type ITooltipProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { checkIsCustomPermission } from 'entities/Permissions'
import {
  EnumUserRoles,
  type IPermissionsContactAccess,
  type Permissions,
} from 'entities/Users/model/types'
import { type IParamsUpdateMemberPermissions } from 'entities/Users/api/types'
import { roleDropdownItems } from 'entities/Users'

type IPermissionItem = {
  key: string
  label: string
  value: boolean | string
  disabled: boolean
  custom: boolean
  tooltip?: {
    label: React.ReactNode
    tooltipProps?: ITooltipProps
  }
}

type IPermissionsGroup = {
  key: string
  label: string
  permissions: IPermissionItem[]
}

export class MemberPermissionsStore {
  private _loading = false
  private _initPermissions: Permissions | null = null
  private _permissions: Permissions | null = null
  private _initRole: EnumUserRoles | null = null
  private _role: EnumUserRoles | null = null
  private _useOrganizationSettings = false
  private _initUseOrganizationSettings = false

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get permissions() {
    return this._permissions
  }

  get role() {
    return this._role
  }

  get isRoleChangeDisabled() {
    return this._entities.subscriptionStore.isPAYGPlan && this._initRole === EnumUserRoles.VIEW_ONLY
  }

  get roleLabel() {
    return this._role ? this.roleItems.find((item) => item.id === this._role)?.label : null
  }

  get useOrganizationSettings() {
    return this._useOrganizationSettings
  }

  get hasCustomPermissions() {
    return this.permissionsArray.some((item) =>
      item.permissions.some((permission) => permission.custom)
    )
  }

  get hasChanges() {
    if (!this.permissions || !this._initPermissions) return false
    let hasPermissionsChanges = false

    const keys = Object.keys(this.permissions) as (keyof Permissions)[]
    for (const key of keys) {
      if (this.permissions[key] !== this._initPermissions[key]) {
        hasPermissionsChanges = true
        break
      }
    }

    return (
      hasPermissionsChanges ||
      this._role !== this._initRole ||
      this._useOrganizationSettings !== this._initUseOrganizationSettings
    )
  }

  get roleItems(): IDropdownItem[] {
    return roleDropdownItems
  }

  get permissionsArray(): IPermissionsGroup[] {
    if (!this.permissions) return []

    return [
      {
        key: 'functionalityAccess',
        label: 'Functionality access',
        permissions: [
          {
            key: 'broadcasts',
            label: 'Can access Broadcasts',
            value: this.isUnavailablePermission('broadcasts') ? false : this.permissions.broadcasts,
            disabled: !!this.isUnavailablePermission('broadcasts'),
            custom: this.isCustomPermission('broadcasts'),
          },
          {
            key: 'campaigns',
            label: 'Can access Campaigns',
            value: this.isUnavailablePermission('campaigns') ? false : this.permissions.campaigns,
            disabled: !!this.isUnavailablePermission('campaigns'),
            custom: this.isCustomPermission('campaigns'),
          },
          {
            key: 'textBot',
            label: 'Can access AI Agent',
            value: this.isUnavailablePermission('textBot') ? false : this.permissions.textBot,
            disabled: !!this.isUnavailablePermission('textBot'),
            custom: this.isCustomPermission('textBot'),
          },
          {
            key: 'triggers',
            label: 'Can access Triggers',
            value: this.isUnavailablePermission('triggers') ? false : this.permissions.triggers,
            disabled: !!this.isUnavailablePermission('triggers'),
            custom: this.isCustomPermission('triggers'),
          },
          {
            key: 'reviews',
            label: 'Can access Reviews',
            value: this.isUnavailablePermission('reviews') ? false : this.permissions.reviews,
            disabled: !!this.isUnavailablePermission('reviews'),
            custom: this.isCustomPermission('reviews'),
          },
          {
            key: 'reportingAndAnalytics',
            label: 'Can access Analytics',
            value: this.isUnavailablePermission('reportingAndAnalytics')
              ? false
              : this.permissions.reportingAndAnalytics,
            disabled: !!this.isUnavailablePermission('reportingAndAnalytics'),
            custom: this.isCustomPermission('reportingAndAnalytics'),
          },
          {
            key: 'phoneChecker',
            label: 'Can access PhoneCheckr',
            value: this.isUnavailablePermission('phoneChecker')
              ? false
              : this.permissions.phoneChecker,
            disabled: !!this.isUnavailablePermission('phoneChecker'),
            custom: this.isCustomPermission('phoneChecker'),
          },
          {
            key: 'cannedMessage',
            label: 'Can access Saved Replies',
            value: this.isUnavailablePermission('cannedMessage')
              ? false
              : this.permissions.cannedMessage,
            disabled: !!this.isUnavailablePermission('cannedMessage'),
            custom: this.isCustomPermission('cannedMessage'),
          },
        ],
      },
      {
        key: 'contactsManagement',
        label: 'Contacts management',
        permissions: [
          {
            key: 'contactCreate',
            label: 'Can create Contacts',
            value: this.isUnavailablePermission('contactCreate')
              ? false
              : this.permissions.contactCreate,
            disabled: !!this.isUnavailablePermission('contactCreate'),
            custom: this.isCustomPermission('contactCreate'),
          },
        ],
      },
      {
        key: 'contactsAccess',
        label: 'Contacts access',
        permissions: [
          {
            key: 'contactAccess',
            label: 'Can access all contacts',
            value: 'can_access_all',
            disabled: !!this.isUnavailablePermission('contactAccess'),
            custom: this.isCustomPermission('contactAccess', 'can_access_all'),
          },
          {
            key: 'contactAccess',
            label: 'Can access contacts assigned to their team',
            value: 'can_access_groups',
            disabled: !!this.isUnavailablePermission('contactAccess'),
            custom: this.isCustomPermission('contactAccess', 'can_access_groups'),
          },
          {
            key: 'contactAccess',
            label: 'Can access contacts assigned to them',
            value: 'can_access_own',
            disabled: !!this.isUnavailablePermission('contactAccess'),
            custom: this.isCustomPermission('contactAccess', 'can_access_own'),
          },
        ],
      },
      {
        key: 'inboxManagement',
        label: 'Inbox management',
        permissions: [
          {
            key: 'inbox',
            label: 'Can manage Inboxes',
            value: this.isUnavailablePermission('inbox') ? false : this.permissions.inbox,
            disabled: !!this.isUnavailablePermission('inbox'),
            custom: this.isCustomPermission('inbox'),
          },
          {
            key: 'transferConversation',
            label: 'Can transfer Conversations',
            value: this.isUnavailablePermission('transferConversation')
              ? false
              : this.permissions.transferConversation,
            disabled: !!this.isUnavailablePermission('transferConversation'),
            custom: this.isCustomPermission('transferConversation'),
          },
          {
            key: 'reassignConversation',
            label: 'Can reassign Conversations',
            value: this.isUnavailablePermission('reassignConversation')
              ? false
              : this.permissions.reassignConversation,
            disabled: !!this.isUnavailablePermission('reassignConversation'),
            custom: this.isCustomPermission('reassignConversation'),
          },
        ],
      },
      {
        key: 'dataAndSecurity',
        label: 'Data & Security',
        permissions: [
          {
            key: 'dataAndSecurity',
            label: 'Can manage Data & Security',
            tooltip: {
              label: (
                <>
                  Can import and export contacts
                  <br />
                  Can manage security settings
                </>
              ),
            },
            value: this.isUnavailablePermission('dataAndSecurity')
              ? false
              : this.permissions.dataAndSecurity,
            disabled: !!this.isUnavailablePermission('dataAndSecurity'),
            custom: this.isCustomPermission('dataAndSecurity'),
          },
          {
            key: 'billing',
            label: 'Can manage Billing',
            value: this.isUnavailablePermission('billing') ? false : this.permissions.billing,
            disabled: !!this.isUnavailablePermission('billing'),
            custom: this.isCustomPermission('billing'),
          },
          {
            key: 'members',
            label: 'Can manage Members',
            value: this.isUnavailablePermission('members') ? false : this.permissions.members,
            disabled: !!this.isUnavailablePermission('members'),
            custom: this.isCustomPermission('members'),
          },
          {
            key: 'manualOptIn',
            label: 'Can access Manual Opt-In',
            value: this.isUnavailablePermission('manualOptIn')
              ? false
              : this.permissions.manualOptIn,
            disabled: !!this.isUnavailablePermission('manualOptIn'),
            custom: this.isCustomPermission('manualOptIn'),
          },
        ],
      },
    ]
  }

  get loading() {
    return this._loading
  }

  get contactAccessValue() {
    return this.permissions?.contactAccess
  }

  get unavailablePermissions() {
    if (!this._role) return null

    return this._entities.permissionsStore?.permissions?.[this._role]?.['unavailable_permissions']
  }

  get paramsUpdatePermissions(): IParamsUpdateMemberPermissions | null {
    if (!this._role || !this._permissions) return null

    return {
      role: this._role,
      permissions: this._permissions,
      use_organization_settings: this._useOrganizationSettings,
    }
  }

  setUseOrganizationSettings = (value: boolean) => {
    this._useOrganizationSettings = value

    if (!this._role) return

    if (value) {
      const defaultPermissions = this.getDefaultPermissions(this._role)
      if (defaultPermissions) {
        this._permissions = defaultPermissions
      }
    }
  }

  isCustomPermission = (
    key: keyof Permissions,
    contactAccessValue?: IPermissionsContactAccess | null
  ) => {
    if (!this._role || !this.permissions) return false

    const rolePermissionsData = this._entities.permissionsStore.permissions?.[this._role] ?? null

    return checkIsCustomPermission(key, this.permissions, rolePermissionsData, contactAccessValue)
  }

  isUnavailablePermission = (key: keyof Permissions) => {
    if (!this._role || !this.permissions) return false
    return this.unavailablePermissions?.find((permission) => permission.key === key)
  }

  cancelPermissions = () => {
    if (!this._role || !this._initPermissions) return

    this._permissions = this._initPermissions
    this._role = this._initRole
    this._useOrganizationSettings = this._initUseOrganizationSettings
  }

  setPermission = (key: string, value: boolean | IPermissionsContactAccess | string) => {
    if (!this._role || !this._permissions) return

    this._permissions = {
      ...this._permissions,
      [key]: value,
    }
  }

  setLoading = (loading: boolean) => {
    this._loading = loading
  }

  getDefaultPermissions = (role: EnumUserRoles): Permissions | null => {
    const organizationPermissions =
      this._entities.permissionsStore.permissions?.[role]?.['organization_permissions']

    if (organizationPermissions) {
      return {
        billing: organizationPermissions.billing.default ?? false,
        broadcasts: organizationPermissions.broadcasts.default ?? false,
        campaigns: organizationPermissions.campaigns.default ?? false,
        contactAccess: organizationPermissions.contactAccess.default ?? 'can_access_own',
        contactCreate: organizationPermissions.contactCreate.default ?? false,
        dataAndSecurity: organizationPermissions.dataAndSecurity.default ?? false,
        inbox: organizationPermissions.inbox.default ?? false,
        manualOptIn: organizationPermissions.manualOptIn.default ?? false,
        members: organizationPermissions.members.default ?? false,
        phoneChecker: organizationPermissions.phoneChecker.default ?? false,
        reportingAndAnalytics: organizationPermissions.reportingAndAnalytics.default ?? false,
        textBot: organizationPermissions.textBot.default ?? false,
        triggers: organizationPermissions.triggers.default ?? false,
        reviews: organizationPermissions.reviews?.default ?? false,
        transferConversation: organizationPermissions.transferConversation?.default ?? false,
        cannedMessage: organizationPermissions.cannedMessage?.default ?? false,
        reassignConversation: organizationPermissions.reassignConversation?.default ?? false,
      }
    }

    return null
  }

  setRole = (role: EnumUserRoles) => {
    this._role = role

    const defaultPermissions = this.getDefaultPermissions(role)
    if (defaultPermissions) {
      this._permissions = defaultPermissions
    }
  }

  init = (
    role: EnumUserRoles,
    permissions: Permissions | null,
    useOrganizationSettings?: boolean
  ) => {
    if (!permissions) {
      const defaultPermissions = this.getDefaultPermissions(role)

      this._permissions = defaultPermissions
      this._initPermissions = defaultPermissions
    } else {
      this._permissions = permissions
      this._initPermissions = permissions
    }

    this._role = role
    this._initRole = role
    this._useOrganizationSettings = useOrganizationSettings ?? false
    this._initUseOrganizationSettings = useOrganizationSettings ?? false
  }

  reset = () => {
    this._permissions = null
    this._role = null
    this._initRole = null
    this._initPermissions = null
    this._useOrganizationSettings = false
    this._initUseOrganizationSettings = false
  }
}
